import {
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  HostListener,
  Input,
  NgZone,
  Output,
  Renderer2
} from '@angular/core';

import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import type { PlacementArray } from '@ng-bootstrap/ng-bootstrap/util/positioning';
import { distinctUntilChanged, filter, first, map, skip, startWith, takeUntil } from 'rxjs';

import {
  BreakpointService,
  BrowserService,
  OverlayWrapperService,
  ResizeService,
  WindowRefService
} from '@hdms/shared/util-core';

import { PopoverDirectiveAbstract } from '../shared/popover-directive.abstract';

const NAMESPACE = 'shrd-uic-flyout';

export const FlyoutClassName = {
  ARROW: 'arrow',
  POPOVER: `${NAMESPACE}-popover`,
  WHITE_ARROW: `white-arrow`,
  AUTO_POSITIONING: 'auto-positioning',
  AUTO_POS_FIXED_WIDTH: 'auto-pos-fixed-width',
  AUTO_POS_END: 'auto-pos-end',
  BODY_FLYOUT_OPEN: 'overflow-hidden-sm-down',
  BTN_FLYOUT_OPEN: 'flyout-open'
};

export enum ContainerPlacement {
  PARENT = 'parent',
  BODY = 'body',
  STICKY_PARENT = 'stickyParent'
}

function getTriggerCenter(rect: DOMRect, offsetLeft: number): number {
  return rect.left + rect.width / 2 - offsetLeft;
}

function setStyle(elm: HTMLElement, prop: string, val: number): void {
  elm && elm.style.setProperty(prop, `${val}px`, 'important');
}

@Directive({
  selector: '[ngbPopover][shrdUicFlyout]',
  exportAs: 'shrdUicFlyout'
})
export class FlyoutDirective extends PopoverDirectiveAbstract {
  @Input() autoPositioning = false;
  @Input() placement: PlacementArray = ['bottom-start'];
  @Input() placeInStickyParent: boolean;
  @Input() openOnClick = true;

  @Output() visibilityChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private readonly win: Window;
  private autoPosFixed: boolean;
  private containerPlacement = ContainerPlacement.BODY;
  private buttonRect: DOMRect;

  @Input()
  set showWhiteArrow(state: boolean) {
    if (state) {
      this.ngbPopover.popoverClass = `${this.ngbPopover.popoverClass} ${FlyoutClassName.WHITE_ARROW}`;
    }
  }

  @Input()
  set autoPosFixedWidth(state: boolean) {
    if (state) {
      this.autoPosFixed = true;
      this.ngbPopover.popoverClass = `${this.ngbPopover.popoverClass} ${FlyoutClassName.AUTO_POS_FIXED_WIDTH}`;
    }
  }

  @HostListener('click') onClick(): void {
    if (!this.ngbPopover.isOpen() && this.openOnClick) {
      this.open();
    }
  }

  @HostListener('hidden') onHidden(): void {
    this.restoreFocus();
    this.resetActiveInstance();
    this.visibilityChange.emit(false);
  }

  constructor(
    elRef: ElementRef,
    private readonly wrapperService: OverlayWrapperService,
    private readonly ngZone: NgZone,
    breakpointService: BreakpointService,
    private readonly browserService: BrowserService,
    private readonly resizeService: ResizeService,
    private readonly renderer: Renderer2,
    @Host() ngbPopover: NgbPopover,
    { nativeWindow }: WindowRefService
  ) {
    super(elRef, ngbPopover, breakpointService);

    this.win = nativeWindow;
    this.configureNgbPopover();
  }

  open(): void {
    this.setButtonRect();
    const stickyParent = this.getStickyParent();
    this.ngbPopover.container = stickyParent ? null : ContainerPlacement.BODY;

    if (stickyParent) {
      this.containerPlacement = this.placeInStickyParent ? ContainerPlacement.STICKY_PARENT : ContainerPlacement.PARENT;
    } else {
      this.containerPlacement = ContainerPlacement.BODY;
    }

    this.ngZone.run(() => {
      this.ngbPopover.open();

      if (this.containerPlacement !== ContainerPlacement.PARENT) {
        this.wrapPopover(stickyParent || this.wrapperService.getWrapper());
      }
    });
  }

  protected override configureNgbPopover(): void {
    this.ngbPopover.placement = this.placement;
    this.ngbPopover.triggers = 'manual';
    this.ngbPopover.popoverClass = `${FlyoutClassName.POPOVER}`;

    super.configureNgbPopover();
  }

  protected override onOpen(): void {
    // Close potentially existing open popover.
    // There's a chance, still another popover is open, e.g.
    // by opening current popover via keyboard navigation trigger.
    PopoverDirectiveAbstract.activeInstance?.close();

    super.onOpen();
    // timeout required to place body class slightly after
    // potential previously opened flyout has been closed
    setTimeout(this.toggleOpenClasses.bind(this));

    this.updatePopoverStyles();

    this.visibilityChange.emit(true);

    this.subscribeToResize();
    this.placeInStickyParent && this.closeOnBreakpointChange();
  }

  protected override onClose(): void {
    super.onClose();
    this.toggleOpenClasses();
  }

  private getChevronElm(): HTMLElement {
    const popoverBody = this.getPopoverElm();

    let arrow: HTMLElement = popoverBody.querySelector(`.${FlyoutClassName.ARROW}`);
    if (!arrow) {
      // create a new arrow div as existing ngb popover-arrow styles will be overwritten by popper.js
      arrow = this.renderer.createElement('div');
      arrow.classList.add(FlyoutClassName.ARROW);
      this.renderer.appendChild(popoverBody, arrow);
    }

    return arrow;
  }

  private getPopoverTopOffset(btnRect: DOMRect, scrollTop: number): number {
    const top = btnRect.top;
    const height = btnRect.height;
    let offset: number;

    switch (this.containerPlacement) {
      case ContainerPlacement.BODY:
        offset = top + height + scrollTop;
        break;
      case ContainerPlacement.STICKY_PARENT:
        offset = top + height - this.getStickyParent().getBoundingClientRect().top;
        break;
      case ContainerPlacement.PARENT:
      default:
        offset = height;
    }

    return offset;
  }

  private updatePopoverStyles(): void {
    if (!this.isWithinDesktopBreakpoint()) {
      return;
    }

    const popoverElm = this.getPopoverElm();
    if (!this.autoPositioning) {
      const offsetTop = this.getPopoverTopOffset(this.buttonRect, this.win.scrollY);
      this.runWhenStable(this.updateChevronStyles.bind(this, offsetTop));
      setStyle(this.popoverBody, 'margin-top', offsetTop);
    } else {
      popoverElm.classList.add(FlyoutClassName.AUTO_POSITIONING);
      setStyle(popoverElm, 'width', this.buttonRect.width);
    }

    if (this.autoPosFixed) {
      // wait for zone to be stable as position calculation relies on flyout body to be fully rendered
      this.runWhenStable(this.updateAutoPosFixedPosition.bind(this, this.buttonRect, this.popoverBody));
    }
  }

  private updateAutoPosFixedPosition(btnRect: DOMRect, popoverBodyElm: HTMLElement): void {
    const popoverOffsetX = this.getPopoverOffsetX();
    const buttonCenter = getTriggerCenter(btnRect, popoverOffsetX);
    const flyoutBodyElm: HTMLElement = popoverBodyElm.querySelector('.shrd-uic-flyout-body');

    // reset previously applied styles/classes
    popoverBodyElm.classList.remove(FlyoutClassName.AUTO_POS_END);
    flyoutBodyElm.removeAttribute('style');

    const popoverBodyRect = popoverBodyElm.getBoundingClientRect();
    const flyoutBodyRect = flyoutBodyElm.getBoundingClientRect();
    const flyoutBodyLeft = buttonCenter - flyoutBodyRect.width / 2 - popoverBodyRect.left + popoverOffsetX;

    if (flyoutBodyLeft + flyoutBodyRect.width > popoverBodyRect.right - popoverOffsetX) {
      popoverBodyElm.classList.add(FlyoutClassName.AUTO_POS_END);
    } else if (flyoutBodyLeft > 0) {
      setStyle(flyoutBodyElm, 'margin-left', flyoutBodyLeft);
    }
  }

  private updateChevronStyles(offsetTop: number): void {
    const chevron = this.getChevronElm();
    setStyle(chevron, 'left', getTriggerCenter(this.buttonRect, this.getPopoverOffsetX()));
    setStyle(chevron, 'margin-top', offsetTop);
  }

  private toggleOpenClasses(): void {
    this.win.document.body.classList.toggle(FlyoutClassName.BODY_FLYOUT_OPEN, this.isOpen);
    this.elRef.nativeElement.classList.toggle(FlyoutClassName.BTN_FLYOUT_OPEN, this.isOpen);
  }

  private getPopoverOffsetX(): number {
    return this.autoPosFixed ? this.getPopoverElm().getBoundingClientRect().left : 0;
  }

  private setButtonRect(): void {
    this.buttonRect = this.getTriggerElm().getBoundingClientRect();
  }

  private runWhenStable(fn: () => void): void {
    this.ngZone.onStable.pipe(first()).subscribe(fn);
  }

  private subscribeToResize(): void {
    this.resizeService.onResize$
      .pipe(
        filter(_ => this.isWithinDesktopBreakpoint()),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(_ => {
        this.setButtonRect();
        this.updatePopoverStyles();
      });
  }

  // Close flyout when switching from mobile to desktop,
  // For now only used when `placeInStickyParent` is set,
  // to avoid complex DOM operations / style recalcs for edge-cases.
  // This still can be refined, if required
  private closeOnBreakpointChange(): void {
    this.breakpointService.breakpointChange$
      .pipe(
        startWith(null),
        filter(_ => this.isOpen),
        map(_ => this.isWithinDesktopBreakpoint()),
        distinctUntilChanged(),
        skip(1),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(_ => this.close());
  }

  /**
   * Restore the focus to the trigger element only if
   * - stored instance is current one
   * - the popover has been closed by the trigger
   * - the currently focused element is a child of the popover
   *
   * Don't set the focus back to current trigger if
   * another flyout/popover triggered current close
   *
   * @private
   */
  private restoreFocus(): void {
    const trigger = this.getTriggerElm();
    const activeElement = this.win.document.activeElement;
    const closedByTrigger = activeElement === trigger;

    if (this.isActiveInstance() && (closedByTrigger || this.isChildOfPopover(activeElement))) {
      trigger.setAttribute('tabindex', '0');
      trigger.focus();
    }
  }
}
